import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Stack, TextField, Typography,
} from '@mui/material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * What this workspace is called, for an account that has exactly one.
 *
 * An account signed up with Google starts out as "Mohsen Khezri's Organisation", and renaming it
 * used to mean opening a list of organisations, finding the only row, and pressing a pencil. The
 * full list is worth it when there are several to tell apart; for one it is a filing cabinet
 * with a single sheet of paper in it. So the simple workspace keeps the one control that
 * mattered and drops the rest, {@code OrganizationsSettings} comes back the moment the account
 * says it runs several numbers.
 */
const WorkspaceNameSettings = () => {
  const { selectedOrganization, renameOrganization } = useOrganization();
  const { can } = usePermissions();
  const { isDemoMode } = useAuth();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const currentName = selectedOrganization?.name || '';
  useEffect(() => { setName(currentName); }, [currentName]);

  const changed = name.trim() !== '' && name.trim() !== currentName;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await renameOrganization(selectedOrganization.id, name.trim());
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Could not save the name');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedOrganization) return null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Workspace name</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What this workspace is called inside FlowGate. Your customers never see it.
      </Typography>

      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Create an account to name your workspace.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && !changed && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}

      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ maxWidth: 520 }}>
        <TextField
          size="small"
          fullWidth
          label="Name"
          value={name}
          disabled={!can.modifyOrg || isDemoMode || saving}
          onChange={(event) => { setName(event.target.value); setSaved(false); }}
          inputProps={{ 'aria-label': 'Workspace name' }}
        />
        <Button
          variant="contained"
          disabled={!changed || !can.modifyOrg || isDemoMode || saving}
          onClick={save}
          sx={{ flexShrink: 0 }}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default WorkspaceNameSettings;
